'use client';

import SecureLS from 'secure-ls';

import { ErrorEventsEnum, errorLogToRemoteUtil } from '../error-logger';

const encryptionSecret =
  process.env.APP_ENCRYPTION_SECRET || 'APP_ENCRYPTION_ERROR_LOGGER';

let securedLocalStorage:
  | SecureLS
  | undefined
  | {
      set: (key: string, value: unknown) => void;
      get: <T>(key: string) => T | null;
      remove: (key: string) => void;
      removeAll: () => void;
    };

const initializeLocalStorageOnWeb = () => {
  /**
   * Utility to provide encryption to items saved to local storage.
   */
  securedLocalStorage = new SecureLS({
    encryptionSecret,
    encodingType: 'aes',
  });
};

/**
 * This is a temporary work around the failing build for SecureLS when
 * localStorage is indexed on the node instead of the browser.
 */
if (global?.localStorage) {
  initializeLocalStorageOnWeb();
}

/**
 * Adds AES Encryption to a value before saving it to local browser storage.
 *
 * @param key The key to fetch.
 * @param value The value to store.
 * @returns boolean Indicate whether success of failure on saving record.
 */
export function saveLocalData(key: string, value: unknown): boolean {
  try {
    securedLocalStorage?.set(key, value);

    return true;
  } catch (error) {
    errorLogToRemoteUtil({
      error,
      errorCode: ErrorEventsEnum.ERROR_IN__LOCAL_OR_MMKV_STORAGE,
      errorTitle: 'Error in Saving to Secure Local Storage ',
      message: 'saveLocalData:failed',
    });

    return false;
  }
}

/**
 * This loads the AES Encrypted value from the browser local storage.
 *
 * @param key The key to fetch.
 * @param value The value to store.
 * @returns T The data saved.
 */
export function loadLocalData<T>(key: string): T | null {
  try {
    return securedLocalStorage?.get(key) || null;
  } catch (error) {
    errorLogToRemoteUtil({
      error,
      errorCode: ErrorEventsEnum.ERROR_IN__LOCAL_OR_MMKV_STORAGE,
      errorTitle: 'Error in reading from Secure Local Storage ',
      message: 'loadLocalData:failed',
    });

    return null;
  }
}

/**
 * The type of the value stored in SecureLS.
 */
type TStoredValue = string | number | boolean;

/**
 * Saves a `string`, `number`, or `boolean` to storage.
 * If you want to store an object, use `saveData` or `saveLocalData`.
 *
 * @param key The key to fetch.
 * @param value The value to store.
 */
export function save(key: string, value: TStoredValue) {
  try {
    securedLocalStorage?.set(key, value);
  } catch (error) {
    errorLogToRemoteUtil({
      error,
      errorCode: ErrorEventsEnum.ERROR_IN__LOCAL_OR_MMKV_STORAGE,
      errorTitle: 'Error in Saving to Secure Local Storage ',
      message: 'save:failed',
    });
  }
}

/**
 * Loads a string from SecureLS storage.
 *
 * @param key The key to fetch.
 */
export function loadString(key: string): string | undefined {
  try {
    const value = securedLocalStorage?.get(key);
    return typeof value === 'string' ? value : undefined;
  } catch (error) {
    errorLogToRemoteUtil({
      error,
      errorCode: ErrorEventsEnum.ERROR_IN__LOCAL_OR_MMKV_STORAGE,
      errorTitle: 'Error in reading from Secure Local Storage ',
      message: 'loadString:failed',
    });

    return undefined;
  }
}

/**
 * Loads a number from SecureLS storage.
 *
 * @param key The key to fetch.
 */
export function loadNumber(key: string): number | undefined {
  try {
    const value = securedLocalStorage?.get(key);
    return typeof value === 'number' ? value : undefined;
  } catch (error) {
    errorLogToRemoteUtil({
      error,
      errorCode: ErrorEventsEnum.ERROR_IN__LOCAL_OR_MMKV_STORAGE,
      errorTitle: 'Error in reading from Secure Local Storage ',
      message: 'loadNumber:failed',
    });

    return undefined;
  }
}

/**
 * Loads a boolean from SecureLS storage.
 *
 * @param key The key to fetch.
 */
export function loadBoolean(key: string): boolean | undefined {
  try {
    const value = securedLocalStorage?.get(key);
    return typeof value === 'boolean' ? value : undefined;
  } catch (error) {
    errorLogToRemoteUtil({
      error,
      errorCode: ErrorEventsEnum.ERROR_IN__LOCAL_OR_MMKV_STORAGE,
      errorTitle: 'Error in reading from Secure Local Storage ',
      message: 'loadBoolean:failed',
    });

    return undefined;
  }
}

/**
 * Loads something from storage and runs it thru JSON.parse.
 * This is useful if what was saved was an object and not of type `TStoredValue`.
 *
 * - Note: This will return `null` if the data could not be parsed.
 *
 * @param key The key to fetch.
 */
export function loadData<T = TStoredValue>(key: string): T | null {
  try {
    const value = securedLocalStorage?.get(key);

    if (value === null || value === undefined) {
      return null;
    }

    // If it's already an object/array, return it directly (SecureLS handles JSON)
    if (typeof value === 'object') {
      return value as T;
    }

    // If it's a string, try to parse it as JSON
    if (typeof value === 'string') {
      return JSON.parse(value) as T;
    }

    // For primitives, return as-is
    return value as T;
  } catch (error) {
    errorLogToRemoteUtil({
      error,
      errorCode: ErrorEventsEnum.ERROR_IN__LOCAL_OR_MMKV_STORAGE,
      errorTitle: 'Error in reading from Secure Local Storage ',
      message: 'loadData:failed',
    });

    return null;
  }
}

/**
 * Delete a specific key + value from SecureLS storage.
 *
 * @param key - The key to delete.
 */
export function removeData(key: string) {
  try {
    securedLocalStorage?.remove(key);
  } catch (error) {
    errorLogToRemoteUtil({
      error,
      errorCode: ErrorEventsEnum.ERROR_IN__LOCAL_OR_MMKV_STORAGE,
      errorTitle: 'Error in removing from Secure Local Storage ',
      message: 'removeData:failed',
    });
  }
}

/**
 * Burn it all to the ground.
 */
export function clearData() {
  try {
    securedLocalStorage?.removeAll();
  } catch (error) {
    errorLogToRemoteUtil({
      error,
      errorCode: ErrorEventsEnum.ERROR_IN__LOCAL_OR_MMKV_STORAGE,
      errorTitle: 'Error in clearing Secure Local Storage ',
      message: 'clearData:failed',
    });
  }
}
